import { z } from "zod";

export const ALLOWED_PAGE_SIZES = [10, 20, 50, 100] as const;
export type AllowedPageSize = (typeof ALLOWED_PAGE_SIZES)[number];

export const listQueryBaseSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z
    .coerce.number()
    .int()
    .refine((v) => (ALLOWED_PAGE_SIZES as readonly number[]).includes(v), {
      message: "invalid pageSize",
    })
    .default(20),
  sort: z.string().optional(),
  q: z.string().optional(),
  withDeleted: z
    .union([z.boolean(), z.literal("true"), z.literal("false")])
    .optional()
    .transform((v) => v === true || v === "true"),
});
export type ListQueryBase = z.infer<typeof listQueryBaseSchema>;

/**
 * An optional yes/no list filter. Query strings carry "true" / "false", and
 * `z.coerce.boolean()` turns every non-empty string into true, so a "No"
 * filter listed the "Yes" rows. Same reading as `withDeleted` above, without
 * its default.
 */
export const queryBoolean = () =>
  z
    .union([z.boolean(), z.literal("true"), z.literal("false")])
    .transform((v) => v === true || v === "true")
    .optional();

export interface ListResult<TRow> {
  rows: TRow[];
  total: number;
  page: number;
  pageSize: number;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Read a comma-separated list of entity ids from a query param, keeping only
 * well-formed uuids. Multi-select filters travel as a single param ("a,b") so
 * the URL stays short and copy-pasteable; malformed parts are dropped rather
 * than handed to the query layer.
 */
export function parseUuidList(raw: string | undefined | null): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  for (const part of raw.split(",")) {
    const value = part.trim();
    if (UUID.test(value)) seen.add(value);
  }
  return [...seen];
}

export function parseSortParam(
  raw: string | undefined,
): { id: string; desc: boolean } | undefined {
  if (!raw) return undefined;
  if (raw.startsWith("-")) return { id: raw.slice(1), desc: true };
  return { id: raw, desc: false };
}
